import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ClipboardList, Users, CheckCircle2, BookOpen, Download } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { MaskLines } from "@/components/Motion";
import { StudentRow } from "@/components/teacher/StudentRow";
import { AssignmentCard } from "@/components/teacher/AssignmentCard";
import { AssignDialog } from "@/components/teacher/AssignDialog";
import { RecordingsSection } from "@/components/teacher/RecordingsSection";
import { RecordingsNotice } from "@/components/teacher/RecordingsNotice";
import { QuizResults } from "@/components/teacher/QuizResults";
import { api, errMsg } from "@/lib/api";
import { downloadCardsPdf, printCards } from "@/lib/loginCards";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";

const Stat = ({ icon: Icon, label, value, id }) => (
  <div className="bg-white border border-[#E5DCCB] p-5 flex items-center justify-between" data-testid={`teacher-stat-${id}`}>
    <div><p className="text-sm text-stone-600">{label}</p><p className="font-display text-4xl font-bold">{value ?? 0}</p></div>
    <Icon className="w-7 h-7 text-[#047857]" />
  </div>
);

export default function Teacher() {
  const { user } = useAuth();
  const { t, lang, dir } = useLang();
  const tt = t.teacher;
  const err = (e) => toast.error(lang === "ar" ? errMsg(e) : t.auth.error);
  const [classes, setClasses] = useState(null);
  const [active, setActive] = useState("");
  const [detail, setDetail] = useState(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cardsBusy, setCardsBusy] = useState(false);

  // Every card gets a fresh password from the server, so the teacher confirms first.
  const makeCards = async (student) => {
    if (!window.confirm(student ? tt.cardConfirm(student.name) : tt.cardsConfirm)) return;
    setCardsBusy(true);
    const id = toast.loading(tt.cardsWorking);
    try {
      const { data } = await api.post(`/teacher/classes/${active}/cards`, { student_ids: student ? [student.id] : [] });
      const d = dir || (lang === "ar" ? "rtl" : "ltr");
      if (student) await printCards(data, tt, d);
      else { await downloadCardsPdf(data, tt, d); toast.success(tt.cardsReady); }
      load();
    } catch (e) { err(e); } finally { toast.dismiss(id); setCardsBusy(false); }
  };

  useEffect(() => {
    api.get("/teacher/classes").then((r) => {
      const list = Array.isArray(r.data) ? r.data : [];
      setClasses(list);
      if (list[0]) setActive(list[0].id);
    }).catch((e) => { setClasses([]); err(e); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(() => {
    if (!active) return;
    api.get(`/teacher/classes/${active}`).then((r) => setDetail(r.data)).catch(err);
  }, [active]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const assign = async (payload) => {
    setSaving(true);
    try {
      await api.post("/teacher/assignments", { ...payload, class_id: active });
      toast.success(tt.assigned);
      setAssignOpen(false);
      load();
    } catch (e) { err(e); } finally { setSaving(false); }
  };

  const remove = async (a) => {
    if (!window.confirm(tt.confirmDelete(a.book?.title || ""))) return;
    try { await api.delete(`/teacher/assignments/${a.id}`); toast.success(tt.deleted); load(); } catch (e) { err(e); }
  };

  const students = Array.isArray(detail?.students) ? detail.students : [];
  const assignments = Array.isArray(detail?.assignments) ? detail.assignments : [];

  return (
    <div className="min-h-screen bg-[#FAF7F2]" data-testid="teacher-page">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-12">
        <p className="text-sm font-semibold text-[#047857] mb-2">{tt.eyebrow} · {user?.name}</p>
        <h1 className="font-display text-4xl sm:text-5xl font-bold mb-8"><MaskLines key={lang} lines={[tt.title]} delay={0.1} /></h1>

        {classes && !classes.length && (
          <p className="p-8 text-center text-stone-600 bg-white border border-[#E5DCCB]" data-testid="teacher-no-classes">{tt.noClasses}</p>
        )}

        {classes?.length > 0 && (
          <>
            <RecordingsNotice />
            <div className="flex gap-2 overflow-x-auto pb-2 mb-8 border-b border-[#E5DCCB]" data-testid="teacher-class-tabs">
              {classes.map((c) => (
                <button key={c.id} onClick={() => setActive(c.id)} data-testid={`teacher-class-tab-${c.id}`}
                  className={`shrink-0 px-5 py-3 font-semibold -mb-[10px] border-b-[3px] transition-colors ${active === c.id ? "border-[#047857] text-[#1C1917]" : "border-transparent text-stone-600 hover:text-[#1C1917]"}`}>
                  {c.name} <span className="text-xs text-stone-600">({c.student_count})</span>
                </button>
              ))}
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
              <Stat icon={Users} label={tt.students} value={students.length} id="students" />
              <Stat icon={CheckCircle2} label={tt.finishedBooks} value={students.reduce((n, s) => n + (s.finished || 0), 0)} id="finished" />
              <Stat icon={BookOpen} label={tt.progressBooks} value={students.reduce((n, s) => n + (s.in_progress || 0), 0)} id="progress" />
              <Stat icon={ClipboardList} label={tt.tasks} value={assignments.length} id="assignments" />
            </div>

            <section className="mb-12">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                <h2 className="font-display text-3xl font-bold">{tt.studentsTitle}</h2>
                <button onClick={() => makeCards(null)} disabled={!students.length || cardsBusy} data-testid="teacher-download-cards-btn"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-[#1C1917] text-white px-6 py-3 font-semibold hover:bg-[#8B3A2B] transition-colors disabled:opacity-50">
                  <Download className="w-5 h-5" /> {tt.cardsBtn}
                </button>
              </div>
              <div className="bg-white border border-[#E5DCCB]" data-testid="teacher-students-list">
                <div className="hidden md:grid grid-cols-[2fr_1fr_1fr_1fr_1fr_auto] gap-3 px-4 py-3 bg-[#F2ECE1] text-sm font-semibold text-stone-700">
                  <span>{tt.colStudent}</span><span>{tt.colLevel}</span><span>{tt.colFinished}</span><span>{tt.colProgress}</span><span>{tt.colLast}</span><span className="w-4" />
                </div>
                {students.map((s) => <StudentRow key={s.id} s={s} onPrint={makeCards} printing={cardsBusy} />)}
                {detail && !students.length && <p className="p-6 text-center text-stone-600">{tt.noStudents}</p>}
              </div>
            </section>

            <section>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                <h2 className="font-display text-3xl font-bold">{tt.tasksTitle}</h2>
                <button onClick={() => setAssignOpen(true)} disabled={!students.length} data-testid="teacher-assign-trigger"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-[#047857] text-white px-6 py-3 font-semibold hover:bg-[#065F46] transition-colors disabled:opacity-50">
                  <ClipboardList className="w-5 h-5" /> {tt.assignBtn}
                </button>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="teacher-assignments-grid">
                {assignments.map((a) => <AssignmentCard key={a.id} a={a} onDelete={remove} />)}
              </div>
              {detail && !assignments.length && <p className="p-6 text-center text-stone-600 bg-white border border-[#E5DCCB]" data-testid="teacher-no-assignments">{tt.noTasks}</p>}
            </section>
            <RecordingsSection classId={active} onError={err} />
            <QuizResults classId={active} />
          </>
        )}
      </div>
      <AssignDialog open={assignOpen} onOpenChange={setAssignOpen} students={students} saving={saving} onSubmit={assign} levels={Array.isArray(detail?.levels) ? detail.levels : []} />
    </div>
  );
}
